import api from "./axiosInstance";

const MFA = { check_mfa: true };
const inflightGets = new Map();

function getKey(url, params) {
	return `${url}?${JSON.stringify(params || {})}`;
}

function connectorPath(connectorId) {
	return `/integrations/connectors/${encodeURIComponent(connectorId)}`;
}

async function getOnce(url, config = {}) {
	const key = getKey(url, config.params);
	let entry = inflightGets.get(key);
	if (!entry) {
		const controller = new AbortController();
		const req = api.get(url, { ...config, signal: controller.signal }).finally(() => {
			const current = inflightGets.get(key);
			if (current?.controller === controller) inflightGets.delete(key);
		});
		entry = { req, controller };
		inflightGets.set(key, entry);
	}
	const { data } = await entry.req;
	return data;
}

/** Cancel in-flight GETs so a later DELETE is not stuck behind a hanging GET. */
export function abortInflightGets(urlPrefix) {
	for (const [key, entry] of [...inflightGets.entries()]) {
		if (!urlPrefix || key.startsWith(urlPrefix)) {
			try {
				entry.controller.abort();
			} catch {}
			inflightGets.delete(key);
		}
	}
}

export async function getIntegrationsCatalog() {
	return getOnce("/integrations/catalog", { params: MFA });
}

export async function listConnectors(params = {}) {
	return getOnce("/integrations/connectors", {
		params: {
			...MFA,
			page: params.page ?? 1,
			page_size: params.page_size ?? 100,
			...(params.type ? { type: params.type } : {}),
			...(params.enabled_only != null ? { enabled_only: params.enabled_only } : {})
		}
	});
}

export async function getConnector(connectorId) {
	return getOnce(connectorPath(connectorId), { params: MFA });
}

export async function createConnector(body) {
	const { data } = await api.post("/integrations/connectors", body, { params: MFA });
	return data;
}

export async function updateConnector(connectorId, body) {
	const { data } = await api.put(`/integrations/connectors/${encodeURIComponent(connectorId)}`, body, { params: MFA });
	return data;
}

export async function deleteConnector(connectorId) {
	const path = connectorPath(connectorId);
	const res = await api.delete(path, {
		params: MFA,
		validateStatus: (status) => status === 204 || (status >= 200 && status < 300)
	});
	// Swagger: 204 No Content on success
	if (res.status === 204 || res.data == null || res.data === "") {
		return { success: true };
	}
	return res.data;
}

export async function patchConnectorEnabled(connectorId, enabled) {
	const { data } = await api.patch(
		`/integrations/connectors/${encodeURIComponent(connectorId)}/enabled`,
		{ enabled: Boolean(enabled) },
		{ params: MFA }
	);
	return data;
}

export async function testConnector(body) {
	const { data } = await api.post("/integrations/connectors/test", body, { params: MFA });
	return data;
}

export async function syncConnector(connectorId, { force_full = false } = {}) {
	const { data } = await api.post(`/integrations/connectors/${encodeURIComponent(connectorId)}/sync`, {}, {
		params: { ...MFA, force_full }
	});
	return data;
}

export async function listSyncJobs(params = {}) {
	return getOnce("/integrations/sync-jobs", {
		params: {
			...MFA,
			page: params.page ?? 1,
			page_size: params.page_size ?? 100,
			...(params.connector_id ? { connector_id: params.connector_id } : {}),
			...(params.range != null && params.range !== "" ? { range: Number(params.range) } : {}),
			...(params.status ? { status: params.status } : {})
		}
	});
}

export function exportSyncJobs(params = {}) {
	const config = {
		params: {
			...MFA,
			...(params.connector_id ? { connector_id: params.connector_id } : {}),
			...(params.range != null && params.range !== "" ? { range: Number(params.range) } : {}),
			...(params.status ? { status: params.status } : {})
		},
		responseType: "blob"
	};
	const key = getKey("/integrations/sync-jobs/export", config.params);
	let entry = inflightGets.get(key);
	if (!entry?.req) {
		const controller = new AbortController();
		const req = api.get("/integrations/sync-jobs/export", { ...config, signal: controller.signal }).finally(() => {
			const current = inflightGets.get(key);
			if (current?.controller === controller) inflightGets.delete(key);
		});
		entry = { req, controller };
		inflightGets.set(key, entry);
	}
	return entry.req;
}
